import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { CheckCircle, RefreshCw, XCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentSuccess = () => {
  const [status, setStatus] = useState('checking'); // checking, success, failed
  const [loading, setLoading] = useState(true);
  const [paymentDetails, setPaymentDetails] = useState(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const sessionId = searchParams.get('session_id');
  const role = localStorage.getItem('signup_role') || 'member';

  useEffect(() => {
    if (sessionId) {
      checkPaymentStatus();
    } else {
      setStatus('failed');
      setLoading(false);
    }
  }, [sessionId]);

  const checkPaymentStatus = async () => {
    let attempts = 0;
    const maxAttempts = 5;
    const pollInterval = 2000; // 2 seconds

    const poll = async () => {
      try {
        const response = await axios.get(`${API}/payments/status/${sessionId}`);
        const data = response.data;
        
        setPaymentDetails(data);
        
        if (data.payment_status === 'paid') {
          setStatus('success');
          setLoading(false);
          return;
        } else if (data.status === 'expired') {
          setStatus('failed');
          setLoading(false);
          return;
        }

        // If payment is still pending and we haven't reached max attempts
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, pollInterval);
        } else {
          setStatus('failed');
          setLoading(false);
        }
      } catch (error) {
        console.error('Error checking payment status:', error);
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, pollInterval);
        } else {
          setStatus('failed');
          setLoading(false);
        }
      }
    };

    poll();
  };

  const continueToPortal = () => {
    // Clear signup data
    localStorage.removeItem('signup_email');
    localStorage.removeItem('signup_role');
    localStorage.removeItem('checkout_session_id');
    
    // Navigate to login page
    navigate('/login');
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="text-center">
          <RefreshCw className="w-16 h-16 text-blue-500 mx-auto mb-6 animate-spin" />
          <h3 className="text-2xl font-semibold text-white mb-4">Processing Payment...</h3>
          <p className="text-gray-300">Please wait while we confirm your payment.</p>
        </div>
      );
    }

    if (status === 'success') {
      return (
        <div className="text-center">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-6" />
          <h3 className="text-2xl font-semibold text-white mb-4">Payment Successful!</h3>
          <p className="text-gray-300 mb-6">
            Welcome to Success Club! Your membership is now active.
          </p>
          
          {paymentDetails && (
            <div className="bg-gray-800 border border-gray-700 rounded-lg p-4 mb-6 text-left">
              <h4 className="text-lg font-semibold text-white mb-2">Payment Details</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-400">Amount:</span>
                  <span className="text-white">${(paymentDetails.amount_total / 100).toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Currency:</span>
                  <span className="text-white">{paymentDetails.currency.toUpperCase()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Status:</span>
                  <span className="text-green-400">Paid</span>
                </div>
              </div>
            </div>
          )}
          
          <div className="bg-amber-600/10 border border-amber-600/20 rounded-lg p-4 mb-6">
            <p className="text-amber-200 text-sm">
              <strong>What's Next:</strong> You can now log in to access your Success Club {role === 'host' ? 'Host' : 'Member'} portal 
              with all the features and benefits of your membership.
            </p>
          </div>
          
          <Button
            onClick={continueToPortal}
            className="w-full bg-amber-600 hover:bg-amber-700 text-white"
          >
            Continue to Login
          </Button>
        </div>
      );
    }

    if (status === 'failed') {
      return (
        <div className="text-center">
          <XCircle className="w-16 h-16 text-red-500 mx-auto mb-6" />
          <h3 className="text-2xl font-semibold text-white mb-4">Payment Failed</h3>
          <p className="text-gray-300 mb-6">
            We couldn't process your payment. Please try again or contact support.
          </p>
          
          <div className="space-y-3">
            <Button
              onClick={() => navigate('/email-verification', { 
                state: { 
                  email: localStorage.getItem('signup_email'),
                  requiresPayment: true 
                } 
              })}
              className="w-full bg-amber-600 hover:bg-amber-700 text-white"
            >
              Try Payment Again
            </Button>
            
            <Button
              onClick={() => navigate('/auth')}
              variant="outline"
              className="w-full border-gray-600 text-gray-300 hover:bg-gray-800"
            >
              Back to Sign Up
            </Button>
          </div>
        </div>
      );
    }
  };

  return (
    <div className="min-h-screen bg-black text-white flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Card className="bg-gray-900 border-gray-800">
          <CardHeader className="text-center">
            <div className="flex items-center justify-center mb-4">
              <div className="w-16 h-16 flex items-center justify-center">
                <img 
                  src="https://customer-assets.emergentagent.com/job_refined-login/artifacts/7hacqtlv_Success%20Club%20Logo%20Files%20-%203%20%281%29.png" 
                  alt="Success Club Logo" 
                  className="w-full h-full object-contain"
                  style={{ filter: 'brightness(0) invert(1)' }}
                />
              </div>
            </div>
            <CardTitle className="text-2xl text-white">
              Payment Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            {renderContent()}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PaymentSuccess;